import { Trans, useTranslation } from 'react-i18next';
import { useTranslationWithNamespace } from '@gov-nx/core/service';
import {
	DataBoxConfirmModalType,
	useDataBoxMessageListContext,
} from '@gov-nx/module/data-box';
import { PageCode } from '@gov-nx/module/page';
import { Button, Dialog, Icon, LayoutGap } from '@gov-nx/ui/web';
import { MarkdownRender } from '../../Markdown/MarkdownRender';

const ExclamationIcon = () => (
	<Icon
		name="exclamation-triangle-fill"
		type="components"
		color="error"
		size="l"
	/>
);

const CancelButton = () => {
	const { getLocalizeCurried } = useTranslationWithNamespace();
	const tnGlobal = getLocalizeCurried('global');

	const { setConfirmModal } = useDataBoxMessageListContext();
	return (
		<Button
			nativeType="button"
			color="primary"
			type="base"
			size="m"
			onClick={() => setConfirmModal(null)}
			aria-label={tnGlobal('akce.zrusit')}>
			{tnGlobal('akce.zrusit')}
		</Button>
	);
};

export const DataBoxMessageListConfirmModal = () => {
	const { getLocalizeCurried } = useTranslationWithNamespace();
	const tn = getLocalizeCurried(PageCode['datove-schranky']);
	const { t } = useTranslation([PageCode['datove-schranky']]);

	const {
		messagesSelect: { selectedMessageList },
		messageListCount,
		massOperations,
		confirmModal,
		setConfirmModal,
		loadingItems,
		isRemoveAllLoading,
	} = useDataBoxMessageListContext();

	return (
		<>
			{confirmModal === DataBoxConfirmModalType.emptyTrash && (
				<Dialog
					isOpen={
						!!confirmModal &&
						[DataBoxConfirmModalType.emptyTrash].includes(confirmModal)
					}
					onClose={() => setConfirmModal(null)}
					icon={<ExclamationIcon />}
					title={
						<h3>
							{tn('zpravy.vypis.trvale-smazani-pocet', {
								pocet: messageListCount,
							})}
						</h3>
					}
					footer={
						<>
							<CancelButton />
							<Button
								nativeType="button"
								color="error"
								type="solid"
								size="m"
								isLoading={loadingItems.removePermanently}
								iconStart={
									!loadingItems.removePermanently && (
										<Icon
											type="templates"
											name="trash"
										/>
									)
								}
								onClick={() => massOperations.handleEmptyTrash()}
								isDisabled={!!loadingItems.removePermanently}
								aria-label={tn('zpravy.trvale-smazat')}>
								{tn('zpravy.trvale-smazat')}
							</Button>
						</>
					}>
					<LayoutGap gap="s">
						<p className="mb-3 text-body-l text-(--text-secondary)">
							{tn('zpravy.vypis.opravdu-trvale-smazat-zobrazene-kopie-pocet', {
								pocet: messageListCount,
							})}
						</p>
						<p>{tn('zpravy.vypis.trvale-odstranite-zobrazene-kopie')}</p>
					</LayoutGap>
				</Dialog>
			)}

			{confirmModal === DataBoxConfirmModalType.removeMessages && (
				<Dialog
					isOpen={
						!!confirmModal &&
						[DataBoxConfirmModalType.removeMessages].includes(confirmModal)
					}
					onClose={() => setConfirmModal(null)}
					icon={<ExclamationIcon />}
					title={
						<h3>
							{tn('zpravy.vypis.smazani-kopii-pocet', {
								pocet: selectedMessageList.length,
							})}
						</h3>
					}
					footer={
						<>
							<CancelButton />
							<Button
								nativeType="button"
								color="error"
								type="outlined"
								size="m"
								isLoading={loadingItems.removePermanently}
								onClick={() => massOperations.handleDeleteMessages(true)}
								isDisabled={!!loadingItems.removePermanently}
								aria-label={tn('zpravy.trvale-smazat')}>
								{tn('zpravy.trvale-smazat')}
							</Button>
							<Button
								nativeType="button"
								color="error"
								type="solid"
								size="m"
								isLoading={loadingItems.remove}
								onClick={() => massOperations.handleDeleteMessages(false)}
								isDisabled={!!loadingItems.remove}
								aria-label={tn('zpravy.smazat')}>
								{tn('zpravy.smazat')}
							</Button>
						</>
					}>
					<LayoutGap gap="s">
						<p>
							{tn('zpravy.vypis.opravdu-smazat-kopie-pocet', {
								pocet: selectedMessageList.length,
							})}
						</p>
						<p>
							<Trans
								t={t}
								i18nKey="zpravy.vypis.smazanim-do-kose"
								components={{ strong: <strong /> }}
							/>
						</p>
						<p>{tn('zpravy.vypis.kopie-muzete-trvale-smazat')}</p>
					</LayoutGap>
				</Dialog>
			)}

			{confirmModal === DataBoxConfirmModalType.removeMessagesPermanently && (
				<Dialog
					isOpen={
						!!confirmModal &&
						[DataBoxConfirmModalType.removeMessagesPermanently].includes(
							confirmModal
						)
					}
					onClose={() => setConfirmModal(null)}
					icon={<ExclamationIcon />}
					title={
						<h3>
							{tn('zpravy.vypis.trvale-smazani-pocet', {
								pocet: selectedMessageList.length,
							})}
						</h3>
					}
					footer={
						<>
							<CancelButton />
							<Button
								nativeType="button"
								color="error"
								type="solid"
								size="m"
								isLoading={loadingItems.removePermanently}
								iconStart={
									!loadingItems.removePermanently && (
										<Icon
											type="templates"
											name="trash"
										/>
									)
								}
								onClick={() => massOperations.handleDeleteMessages(true)}
								isDisabled={!!loadingItems.removePermanently}
								aria-label={tn('zpravy.trvale-smazat')}>
								{tn('zpravy.trvale-smazat')}
							</Button>
						</>
					}>
					<LayoutGap gap="s">
						<p>
							{tn('zpravy.vypis.opravdu-trvale-smazat-kopie-pocet', {
								pocet: selectedMessageList.length,
							})}
						</p>
						<p>{tn('zpravy.vypis.trvale-odstranite-vybrane.kopie')}</p>
					</LayoutGap>
				</Dialog>
			)}
			{confirmModal === DataBoxConfirmModalType.removeAllMessages && (
				<Dialog
					isOpen={
						!!confirmModal &&
						[DataBoxConfirmModalType.removeAllMessages].includes(confirmModal)
					}
					onClose={() => setConfirmModal(null)}
					icon={<ExclamationIcon />}
					title={<h3>{tn('zpravy.vypis.smazani-vsech-zprav')}</h3>}
					footer={
						<>
							<CancelButton />
							<Button
								nativeType="button"
								color="error"
								type="outlined"
								size="m"
								isLoading={loadingItems.removePermanently}
								onClick={massOperations.handleDeleteAllMessagesPermanently}
								isDisabled={isRemoveAllLoading}
								aria-label={tn('zpravy.vypis.smazat-vsechny-trvale')}>
								{tn('zpravy.vypis.smazat-vsechny-trvale')}
							</Button>
							<Button
								nativeType="button"
								color="error"
								type="solid"
								size="m"
								isLoading={loadingItems.remove}
								onClick={massOperations.handleDeleteAllMessages}
								isDisabled={isRemoveAllLoading}
								aria-label={tn('zpravy.vypis.smazat-vsechny')}>
								{tn('zpravy.vypis.smazat-vsechny')}
							</Button>
						</>
					}>
					<LayoutGap gap="s">
						<p>{tn('zpravy.vypis.opravdu-smazat-vsechny-kopie-zprav')}</p>

						<MarkdownRender>
							{tn('zpravy.vypis.odstranite-vsechny-kopie-zprav')}
						</MarkdownRender>
					</LayoutGap>
				</Dialog>
			)}
		</>
	);
};
